import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { Subject, first, finalize } from 'rxjs';
import { LegalizationService } from '../../../data/legalization.service';
import { LegalizationDocumentBasicDto, LegalizationDocumentDto, CorrectLegalizationDocumentBasicDto } from '../../../models/dtos/legalization-document-files-dto';
import { LegalizationDocumentFileTypeDto } from '../../../models/dtos/legalization-document-files-type-dto';
import { AddLegalizationInformationModalData } from '../AddLegalizationInformationModalData';
import { SaveLegalizationInfoBaseComponent } from '../base/save-legalization-info-base.component';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';

@Component({
  selector: 'app-save-legalization-info-by-employee',
  templateUrl: './save-legalization-info-by-employee.component.html',
  styleUrl: './save-legalization-info-by-employee.component.scss',
  standalone: false
})
export class SaveLegalizationInfoByEmployeeComponent extends SaveLegalizationInfoBaseComponent implements OnInit, OnDestroy {
  dialogRef = inject<MatDialogRef<AddLegalizationInformationModalData>>(MatDialogRef);
  data = inject<AddLegalizationInformationModalData>(MAT_DIALOG_DATA);
  private formBuilder = inject(UntypedFormBuilder);
  private spinner = inject(NgxSpinnerService);
  private legalizationService = inject(LegalizationService);
  private snackbar = inject(SnackBarService);

  public isDocumentUploaded: boolean = false;
  public formGroup: UntypedFormGroup;

  private legalizationFileTypeName: string;

  private readonly unsubscribe$ = new Subject<void>();

  selectedLegalizationTypeId: number | null = null;

  constructor() {
    super();
  }

  get legalizationDocumentTypeId() {
    return this.formGroup.get('legalizationDocumentTypeId') as UntypedFormControl;
  }
  get description() {
    return this.formGroup.get('description') as UntypedFormControl;
  }

  ngOnInit(): void {
    this.formGroup = this.formBuilder.group({
      legalizationDocumentTypeId: [null, [Validators.required]],
      description: [null]
    });

    if (this.data.LegalizationDocumentId) {
      this.loadLegalizationDocument(this.data.LegalizationDocumentId);
    }
  }

  isSendWorkerFileButtonEnabled(): boolean {
    return this.isDocumentUploaded &&
      this.isFileCountValid() &&
      !!this.legalizationDocumentTypeId.value &&
      this.formGroup.valid;
  }

  createLegalizationDocument(): void {
    if (!this.files || this.formGroup.invalid) {
      return;
    }

    let legalizationDocument: LegalizationDocumentBasicDto = {
      WorkerId: this.data.WorkerId,
      LegalizationDocumentTypeId: this.legalizationDocumentTypeId.value,
      Files: this.result,
      Name: this.legalizationFileTypeName,
      LegalizationId: this.data.LegalizationId
    };

    this.onSave(legalizationDocument);
  }

  onClose(): void {
    this.dialogRef.close(false);
  }

  onSave(legalizationInformation: LegalizationDocumentBasicDto): void {
    this.spinner.show();

    const handleSuccess = () => {
      if (this.data.IsCorrection) {
        this.snackbar.openSuccessSnackBar('Legalization.SuccessfullyCorrected');
      }
      else {
        this.snackbar.openSuccessSnackBar('Legalization.SuccessfullySaved');
      }
      this.files = [];
      this.dialogRef.close({ isSuccess: true });
    };

    const observable = this.data.IsCorrection
      ? this.legalizationService.correctLegalizationDocumentByEmployee({
        Id: this.data.LegalizationDocumentId!,
        Files: legalizationInformation.Files
      } as CorrectLegalizationDocumentBasicDto)
      : this.legalizationService.saveLegalizationDocumentByEmployee(legalizationInformation);

    observable.pipe(
      first(),
      finalize(() => this.spinner.hide()))
      .subscribe({
        next: () => handleSuccess(),
      });
  }

  onAddDocument(legalizationDocument: LegalizationDocumentDto): void {
    this.files = [];
    this.dialogRef.close({ isSuccess: true, legalizationDocument: legalizationDocument });
  }

  onLegalizationTypeSelected(type: LegalizationDocumentFileTypeDto): void {
    this.legalizationDocumentTypeId.setValue(type.Id);
    this.legalizationFileTypeName = type.Name;
    this.description.setValue(type.Description);
    this.selectedLegalizationTypeId = type.Id;
  }

  private loadLegalizationDocument(documentId: number): void {
    this.spinner.show();
    this.legalizationService.getLegalizationDocumentByEmployee(documentId)
      .pipe(
        first(),
        finalize(() => this.spinner.hide())
      )
      .subscribe(document => {
        if (document) {
          this.selectedLegalizationTypeId = document.LegalizationDocumentTypeId;
          this.legalizationDocumentTypeId.setValue(this.selectedLegalizationTypeId);
          this.description.setValue(document.Description);
        }
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }
}
